/**
 * Architecture layout (pure, sim yards): turns a DungeonDef + theme into solids the builder can
 * mesh, without touching the walkable geometry.
 *
 *  - floors: every room polygon at its elevation, corridor strips outside the rooms split into
 *    flat landings and stair steps (one piece per step) following CorridorDef.rises
 *  - ledges: a raised dressing band around each room (walls move outward by the room's margin)
 *    and cheek ledges along corridors; all clutter stands on them, never on walkable floor
 *  - walls: tiled along the outer boundary of rooms+ledges, corridors+cheeks and hero alcoves,
 *    open where those regions meet (corridor mouths); heights by facing (far tall, near low)
 *  - posts: corner pillars and opening posts
 *  - hints: authored hero props nudged / scaled to fit their ledge (alcoves where there's room)
 *  - pools: poison basins cut into the ledges
 *
 * Rooms are expected to be axis-aligned rectangles (the Venom Altar's are); other rooms get a
 * uniform convex offset or no ledge. Everything is deterministic.
 */
import type { CorridorDef, DungeonDef, PropHint, RoomDef } from '@keystone/sim';
import { corridorHeightAt, groundHeightAt } from '@keystone/content';
import { computeWallLayout } from '../layout';
import type { CeilingKind, DungeonTheme, HeroSpec, RoomStyle } from '../themes/types';
import { type Plane, type Poly, type Pt, bbox, ccw, centroid, clipHalfPlane, convexDifference, convexOverlap, distToPoly, fitPlane, offsetConvex, planeAt, pointInConvex, pointInPoly, rectPoly, subtractAll } from './geom2d';

export type WallFacing = 'far' | 'near' | 'side';

export interface Rect {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

export interface FloorPiece {
  poly: Poly;
  /** Height per vertex. */
  h: number[];
  floor: string;
  owner: string;
  cluster: number;
  /** A stair step (its exposed sides are risers). */
  step?: boolean;
  room?: boolean;
}

export interface LedgePiece {
  poly: Poly;
  h: number[];
  plane: Plane;
  owner: string;
  cluster: number;
  corridor?: boolean;
  box: Rect;
}

export interface WallPiece {
  /** Inner face line (on the region boundary); the wall extends `thick` along `n`. */
  a: Pt;
  b: Pt;
  /** Outward unit normal. */
  n: Pt;
  thick: number;
  base: number;
  top: number;
  /** Ledge (or floor) height at the wall's inner face. */
  foot: number;
  facing: WallFacing;
  owner: string;
  cluster: number;
  corridor: boolean;
  /** Enclosed: the camera cutaway cuts the wall down to this height (also its camera-collision top). */
  cutY: number;
}

export interface Post {
  pos: Pt;
  size: number;
  base: number;
  top: number;
  owner: string;
  cluster: number;
  kind: 'corner' | 'opening' | 'pillar' | 'timber';
  /** Cutaway stub height (and camera-collision top). */
  cutY: number;
  /** Outward normal of the wall it stands against (cutaway: near-side posts cut like near walls). */
  n?: Pt;
}

/** Enclosed: the thick wall over a corridor mouth, with an arched underside (spring → apex). */
export interface Lintel {
  /** Opening on the room's wall line; the lintel extends `thick` along `n` (outward). */
  a: Pt;
  b: Pt;
  n: Pt;
  thick: number;
  /** Underside height at the opening's sides and at its middle. */
  spring: number;
  apex: number;
  top: number;
  foot: number;
  owner: string;
  cluster: number;
}

/**
 * Enclosed: an overhead along an axis a→b, `span` wide (centred on the axis). Vaults / caves
 * spring at `springA`..`springB` (linear along the axis) and rise `rise` at the middle; 'beams'
 * ceilings are flat plank roofs at the spring height (+ the beams below them).
 */
export interface Ceiling {
  kind: 'vault' | 'beams' | 'cave';
  a: Pt;
  b: Pt;
  span: number;
  springA: number;
  springB: number;
  rise: number;
  /** Transverse rib / beam spacing along the axis (yd). */
  ribEvery: number;
  owner: string;
  cluster: number;
}

/** Enclosed: an overhead timber beam / architrave from a to b (bottom at `y`). */
export interface Beam {
  a: Pt;
  b: Pt;
  y: number;
  h: number;
  w: number;
  wood: boolean;
  owner: string;
  cluster: number;
}

/** Enclosed: a sloped lean-to roof slab (the cloister arcade), heights per vertex. */
export interface Roof {
  poly: Poly;
  h: number[];
  owner: string;
  cluster: number;
}

export interface PoolCut {
  poly: Poly;
  center: Pt;
  /** Facing (sim radians) toward the room. */
  yaw: number;
  half: [number, number];
  waterY: number;
  rimY: number;
  owner: string;
  cluster: number;
}

export interface RoomArch {
  id: string;
  def: RoomDef;
  style: RoomStyle;
  rect: Rect | null;
  /** Ledge outer boundary (room + margins), or the room polygon when there's no ledge. */
  outer: Poly;
  margins: { w: number; e: number; s: number; n: number };
  elevation: number;
  ledgeTop: number;
  cluster: number;
  alcoves: Poly[];
}

export interface CorridorArch {
  id: string;
  def: CorridorDef;
  strip: Poly;
  wide: Poly;
  cluster: number;
  floor: string;
  axis: { a: Pt; b: Pt; dir: Pt; len: number; width: number };
}

export interface PlacedHint {
  hint: PropHint;
  kind: string;
  spec: HeroSpec;
  pos: Pt;
  /** Facing (sim radians). */
  yaw: number;
  scale: number;
  /** Surface height under the prop. */
  y: number;
  owner: string;
  cluster: number;
  /** Footprint polygon (sim yards). */
  foot: Poly;
  onLedge: boolean;
  /** Which side of the room it sits on (for wall-facing rules). */
  facing: WallFacing | null;
}

export interface Region {
  poly: Poly;
  owner: string;
  cluster: number;
  kind: 'room' | 'alcove' | 'corridor';
}

export interface ArchLayout {
  rooms: RoomArch[];
  corridors: CorridorArch[];
  floors: FloorPiece[];
  ledges: LedgePiece[];
  walls: WallPiece[];
  posts: Post[];
  /** Enclosed interiors (theme.arch.enclosed): lintels, ceilings, beams, arcade roofs. */
  enclosed: boolean;
  lintels: Lintel[];
  ceilings: Ceiling[];
  beams: Beam[];
  roofs: Roof[];
  pools: PoolCut[];
  regions: Region[];
  walkable: Poly[];
  hints: PlacedHint[];
  clusterOf: ReadonlyMap<string, number>;
  clusterCount: number;
  baseY: number;
  ledgeHeight: number;
  /** Ledge top at p, or null off the ledges. */
  ledgeTopAt(p: Pt): number | null;
  /** Floor height at p (groundHeightAt: linear across stairs, what units stand on). */
  floorAt(p: Pt): number;
  /** Height of the rendered floor at p (stair steps are stepped). */
  renderedFloorAt(p: Pt): number;
  /** What's at p: walkable floor, ledge, pool, or nothing (inside a wall / void). */
  surfaceAt(p: Pt): { kind: 'floor' | 'ledge' | 'pool' | 'void'; h: number };
  inWalkable(p: Pt): boolean;
  /** Inside a DungeonDef obstacle (a pillar or the fountain: solid, not floor). */
  inObstacle(p: Pt): boolean;
  /** Distance from p to the nearest walkable polygon (0 inside). */
  distToWalkable(p: Pt): number;
}

const WALL_CLEAR = 0.8;

export function rectOf(poly: readonly Pt[]): Rect | null {
  if (poly.length !== 4) return null;
  const b = bbox(poly);
  const onCorner = poly.every((p) => (Math.abs(p.x - b.x0) < 1e-6 || Math.abs(p.x - b.x1) < 1e-6) && (Math.abs(p.y - b.y0) < 1e-6 || Math.abs(p.y - b.y1) < 1e-6));
  return onCorner ? b : null;
}

const facingOf = (n: Pt): WallFacing => (n.y > 0.6 ? 'far' : n.y < -0.6 ? 'near' : 'side');

function isConvex(p: readonly Pt[]): boolean {
  const n = p.length;
  if (n < 3) return false;
  let sign = 0;
  for (let i = 0; i < n; i++) {
    const a = p[i]!;
    const b = p[(i + 1) % n]!;
    const c = p[(i + 2) % n]!;
    const z = (b.x - a.x) * (c.y - b.y) - (b.y - a.y) * (c.x - b.x);
    if (Math.abs(z) < 1e-9) continue;
    if (sign === 0) sign = Math.sign(z);
    else if (Math.sign(z) !== sign) return false;
  }
  return true;
}

export function buildArch(d: DungeonDef, theme: DungeonTheme, extraHints: readonly PropHint[] = []): ArchLayout {
  const A = theme.arch;
  const ledgeH = A.ledgeHeight;
  const cm = ledgeH > 0 ? A.corridorMargin : 0;
  const E = A.enclosed ?? null;
  const roomStyleById = new Map(d.rooms.map((r) => [r.id, theme.room(r, d)]));
  const corridorStyleById = new Map(d.corridors.map((c) => [c.id, theme.corridorStyle?.(c, d) ?? {}]));
  const roomWallH = (id: string): number => roomStyleById.get(id)?.wallH ?? E?.roomWallH ?? 4;
  const corridorWallH = (id: string): number => corridorStyleById.get(id)?.wallH ?? E?.corridorWallH ?? 3;
  const avgNormal = (ws: readonly WallPiece[]): Pt | undefined => {
    if (!ws.length) return undefined;
    const x = ws.reduce((s, w) => s + w.n.x, 0);
    const y = ws.reduce((s, w) => s + w.n.y, 0);
    const l = Math.hypot(x, y);
    return l > 1e-6 ? { x: x / l, y: y / l } : undefined;
  };
  const postCut = (ws: readonly WallPiece[], p: Pt): number => (ws.length ? Math.max(...ws.map((w) => w.cutY)) : E ? floorAt(p) + ledgeH + E.cutHeight : Infinity);

  // ---- clusters ------------------------------------------------------------------
  const clusterOf = new Map<string, number>();
  let clusterCount = 0;
  for (const group of theme.clusters ?? []) {
    const live = group.filter((id) => d.rooms.some((r) => r.id === id));
    if (!live.length) continue;
    for (const id of live) clusterOf.set(id, clusterCount);
    clusterCount++;
  }
  for (const r of d.rooms) if (!clusterOf.has(r.id)) clusterOf.set(r.id, clusterCount++);
  for (const c of d.corridors) clusterOf.set(c.id, clusterOf.get(c.from) ?? clusterOf.get(c.to) ?? 0);
  if (clusterCount === 0) clusterCount = 1;

  // ---- rooms and margins -----------------------------------------------------------
  const roomRects = d.rooms.map((r) => rectOf(r.shape.points));
  const capFor = (ri: number, side: 'w' | 'e' | 's' | 'n', lo: number, hi: number): number => {
    const R = roomRects[ri]!;
    let cap = 14;
    roomRects.forEach((O, oi) => {
      if (oi === ri || !O) return;
      let gap = -1;
      if (side === 'w' || side === 'e') {
        if (O.y1 < lo - 0.5 || O.y0 > hi + 0.5) return;
        gap = side === 'w' ? R!.x0 - O.x1 : O.x0 - R!.x1;
      } else {
        if (O.x1 < lo - 0.5 || O.x0 > hi + 0.5) return;
        gap = side === 's' ? R!.y0 - O.y1 : O.y0 - R!.y1;
      }
      if (gap >= -0.01) cap = Math.min(cap, Math.max(0, gap / 2 - 0.3));
    });
    return cap;
  };

  const rooms: RoomArch[] = d.rooms.map((r, i) => {
    const style = theme.room(r, d);
    const R = roomRects[i] ?? null;
    const want = ledgeH > 0 ? (style.margin ?? A.roomMargin) : 0;
    const elevation = r.elevation ?? 0;
    const margins = { w: 0, e: 0, s: 0, n: 0 };
    let outer: Poly = ccw(r.shape.points);
    if (R && want > 0) {
      margins.w = Math.min(want, capFor(i, 'w', R.y0, R.y1));
      margins.e = Math.min(want, capFor(i, 'e', R.y0, R.y1));
      margins.s = Math.min(want, capFor(i, 's', R.x0, R.x1));
      margins.n = Math.min(want, capFor(i, 'n', R.x0, R.x1));
      outer = rectPoly(R.x0 - margins.w, R.y0 - margins.s, R.x1 + margins.e, R.y1 + margins.n);
    } else if (want > 0 && isConvex(r.shape.points)) {
      // Uniform offset, capped by the nearest other room.
      let cap = want;
      for (const o of d.rooms) if (o !== r) for (const p of o.shape.points) cap = Math.min(cap, Math.max(0, distToPoly(p, r.shape.points) / 2 - 0.3));
      if (cap > 0.3) {
        outer = offsetConvex(ccw(r.shape.points), cap);
        margins.w = margins.e = margins.s = margins.n = cap;
      }
    }
    return { id: r.id, def: r, style, rect: R, outer, margins, elevation, ledgeTop: elevation + ledgeH, cluster: clusterOf.get(r.id)!, alcoves: [] };
  });

  // ---- corridors ---------------------------------------------------------------------
  const corridors: CorridorArch[] = d.corridors.map((c) => {
    const strip = ccw(c.shape.points);
    const ax = corridorAxis(strip);
    return { id: c.id, def: c, strip, wide: cm > 0 ? offsetConvex(strip, cm) : strip, cluster: clusterOf.get(c.id)!, floor: theme.corridorFloor(c, d), axis: ax };
  });

  const walkable: Poly[] = [...d.rooms.map((r) => ccw(r.shape.points)), ...corridors.map((c) => c.strip)];
  const walkBoxes = walkable.map((p) => bbox(p));
  const inWalkable = (p: Pt): boolean => walkable.some((w, i) => inBox(walkBoxes[i]!, p, 0) && pointInPoly(p, w));
  const distToWalkable = (p: Pt): number => {
    let best = Infinity;
    walkable.forEach((w, i) => {
      const b = walkBoxes[i]!;
      const dx = Math.max(b.x0 - p.x, 0, p.x - b.x1);
      const dy = Math.max(b.y0 - p.y, 0, p.y - b.y1);
      if (Math.hypot(dx, dy) >= best) return;
      best = Math.min(best, distToPoly(p, w));
    });
    return best;
  };
  const floorAt = (p: Pt): number => groundHeightAt(d, p.x, p.y);

  // ---- authored hints: nudge / scale into the ledge, alcoves where neighbours allow ---------
  const hints: PlacedHint[] = [];
  const allHints = [...(d.props ?? []), ...extraHints];
  const pools: PoolCut[] = [];
  for (const hint of allHints) {
    const spec = theme.heroes[hint.kind];
    if (!spec) continue;
    let scale = hint.scale ?? 1;
    if (spec.floorDecal) {
      const owner = hint.roomId ?? '';
      hints.push({ hint, kind: hint.kind, spec, pos: { ...hint.pos }, yaw: hint.facing ?? 0, scale, y: floorAt(hint.pos), owner, cluster: clusterOf.get(owner) ?? 0, foot: [], onLedge: false, facing: null });
      continue;
    }
    if (spec.opening) {
      const owner = hint.corridorId ?? hint.roomId ?? '';
      hints.push({ hint, kind: hint.kind, spec, pos: { ...hint.pos }, yaw: hint.facing ?? 0, scale, y: floorAt(hint.pos), owner, cluster: clusterOf.get(owner) ?? 0, foot: [], onLedge: false, facing: null });
      continue;
    }
    const ri = d.rooms.findIndex((r) => r.id === hint.roomId);
    const room = ri >= 0 ? rooms[ri]! : null;
    const R = room?.rect ?? null;
    const pos = { ...hint.pos };
    let yaw = hint.facing ?? 0;
    if (hint.facing === undefined && R) yaw = Math.atan2((R.y0 + R.y1) / 2 - pos.y, (R.x0 + R.x1) / 2 - pos.x);
    const c = Math.abs(Math.cos(yaw));
    const s = Math.abs(Math.sin(yaw));
    const halfOf = (k: number): [number, number] => [(c * spec.half[1] + s * spec.half[0]) * k, (s * spec.half[1] + c * spec.half[0]) * k];
    let [hx, hy] = halfOf(scale);
    let facing: WallFacing | null = null;
    if (room && R && ledgeH > 0) {
      const fit = (axis: 'x' | 'y'): void => {
        const lo = axis === 'x' ? R.x0 : R.y0;
        const hi = axis === 'x' ? R.x1 : R.y1;
        const v = axis === 'x' ? pos.x : pos.y;
        if (v >= lo && v <= hi) return;
        const side = axis === 'x' ? (v < lo ? 'w' : 'e') : v < lo ? 's' : 'n';
        const sign = v < lo ? -1 : 1;
        const edge = v < lo ? lo : hi;
        const half = axis === 'x' ? hx : hy;
        const along = axis === 'x' ? [pos.y - hy - 1, pos.y + hy + 1] : [pos.x - hx - 1, pos.x + hx + 1];
        const margin = room.margins[side];
        let out = Math.abs(v - edge);
        const need = out + half + WALL_CLEAR;
        if (need > margin) {
          const cap = capFor(ri, side, along[0]!, along[1]!);
          let depth = Math.min(cap, need);
          if (need > cap) {
            // Shrink to fit if even the nudged prop can't stand between the floor and the wall.
            const room2 = cap - WALL_CLEAR - 0.4;
            if (room2 < 2 * half) {
              const k = Math.max(0.35, room2 / Math.max(0.01, 2 * half));
              scale *= k;
              [hx, hy] = halfOf(scale);
            }
            const h2 = axis === 'x' ? hx : hy;
            out = Math.max(h2 + 0.4, cap - h2 - WALL_CLEAR);
            depth = cap;
          }
          if (depth > margin + 0.05) {
            const [a0, a1] = along as [number, number];
            const e0 = edge;
            const e1 = edge + sign * depth;
            room.alcoves.push(axis === 'x' ? rectPoly(Math.min(e0, e1), a0, Math.max(e0, e1), a1) : rectPoly(a0, Math.min(e0, e1), a1, Math.max(e0, e1)));
          }
        }
        // Never closer to the floor than the footprint allows.
        out = Math.max(out, (axis === 'x' ? hx : hy) + 0.4);
        if (axis === 'x') pos.x = edge + sign * out;
        else pos.y = edge + sign * out;
        if (axis === 'y') facing = sign > 0 ? 'far' : 'near';
        else if (!facing) facing = 'side';
      };
      fit('x');
      fit('y');
      // Props on a ledge face the room (the floor they overlook), whatever the hint says.
      const ox = pos.x < R.x0 ? R.x0 - pos.x : pos.x > R.x1 ? pos.x - R.x1 : 0;
      const oy = pos.y < R.y0 ? R.y0 - pos.y : pos.y > R.y1 ? pos.y - R.y1 : 0;
      if (ox > oy) yaw = pos.x < R.x0 ? 0 : Math.PI;
      else if (oy > 0) yaw = pos.y < R.y0 ? Math.PI / 2 : -Math.PI / 2;
    }
    const foot = rectPoly(pos.x - hx, pos.y - hy, pos.x + hx, pos.y + hy);
    const owner = hint.roomId ?? '';
    const cluster = clusterOf.get(owner) ?? 0;
    if (hint.kind === 'poison_pool') {
      const half: [number, number] = [spec.half[0] * scale, spec.half[1] * scale];
      const top = (room?.ledgeTop ?? floorAt(pos)) as number;
      pools.push({ poly: foot, center: pos, yaw, half, waterY: top - 0.32, rimY: top, owner, cluster });
    }
    // A hint inside the room itself (the cloister fountain, standing in its obstacle) sits on the floor.
    const inRoom = !!R && pos.x > R.x0 && pos.x < R.x1 && pos.y > R.y0 && pos.y < R.y1;
    hints.push({ hint, kind: hint.kind, spec, pos, yaw, scale, y: room && !inRoom ? room.ledgeTop : floorAt(pos), owner, cluster, foot, onLedge: !!room && ledgeH > 0 && !inRoom, facing });
  }

  // ---- regions (for walls) ------------------------------------------------------------
  const regions: Region[] = [];
  for (const r of rooms) regions.push({ poly: r.outer, owner: r.id, cluster: r.cluster, kind: 'room' });
  for (const r of rooms) for (const a of r.alcoves) regions.push({ poly: a, owner: r.id, cluster: r.cluster, kind: 'alcove' });
  for (const c of corridors) regions.push({ poly: c.wide, owner: c.id, cluster: c.cluster, kind: 'corridor' });

  // ---- floors -------------------------------------------------------------------------
  const floors: FloorPiece[] = [];
  const roomPolys = d.rooms.map((r) => ccw(r.shape.points));
  for (const r of rooms) floors.push({ poly: ccw(r.def.shape.points), h: r.def.shape.points.map(() => r.elevation), floor: r.style.floor, owner: r.id, cluster: r.cluster, room: true });
  for (const c of corridors) {
    let pieces = subtractAll([c.strip], roomPolys);
    // Other corridors that overlap this one keep their own floor.
    for (const o of corridors) if (o !== c && corridors.indexOf(o) < corridors.indexOf(c)) pieces = subtractAll(pieces, [o.strip]);
    for (const rise of c.def.rises ?? []) pieces = splitAtRise(pieces, rise.start, rise.end, rise.kind === 'stairs' ? (rise.steps ?? 6) : 1);
    for (const p of pieces) {
      const cen = centroid(p);
      const rise = (c.def.rises ?? []).find((r) => tAlong(cen, r.start, r.end) > 0 && tAlong(cen, r.start, r.end) < 1);
      if (rise && rise.kind === 'stairs') {
        const n = Math.max(1, rise.steps ?? 6);
        const k = Math.min(n - 1, Math.floor(tAlong(cen, rise.start, rise.end) * n));
        const h = rise.startElevation + ((rise.endElevation - rise.startElevation) * (k + 0.5)) / n;
        floors.push({ poly: p, h: p.map(() => h), floor: c.floor, owner: c.id, cluster: c.cluster, step: true });
      } else {
        floors.push({ poly: p, h: p.map((q) => corridorHeightAt(d, c.def, q)), floor: c.floor, owner: c.id, cluster: c.cluster });
      }
    }
  }

  // ---- ledges -----------------------------------------------------------------------
  const ledges: LedgePiece[] = [];
  const wides = corridors.map((c) => c.wide);
  const poolPolys = pools.map((p) => p.poly);
  const addLedge = (poly: Poly, h: number[], owner: string, cluster: number, corridor = false): void => {
    if (poly.length < 3) return;
    ledges.push({ poly, h, plane: fitPlane(poly, h), owner, cluster, corridor, box: bbox(poly) });
  };
  if (ledgeH > 0) {
    for (const r of rooms) {
      const R = r.rect;
      let bands: Poly[] = [];
      if (R) {
        const O = bbox(r.outer);
        bands = [rectPoly(O.x0, R.y1, O.x1, O.y1), rectPoly(O.x0, O.y0, O.x1, R.y0), rectPoly(O.x0, R.y0, R.x0, R.y1), rectPoly(R.x1, R.y0, O.x1, R.y1)].filter((b) => polyArea(b) > 0.05);
      } else if (r.outer !== r.def.shape.points) {
        bands = convexDifference(r.outer, ccw(r.def.shape.points));
      }
      const otherOuters = rooms.filter((o) => o !== r).map((o) => o.outer);
      const alcoves = subtractAll(r.alcoves, [r.outer, ...otherOuters]);
      const pieces = subtractAll([...bands, ...alcoves], [...walkable, ...wides, ...poolPolys]);
      for (const p of pieces) addLedge(p, p.map(() => r.ledgeTop), r.id, r.cluster);
    }
    for (const c of corridors) {
      let cheeks = convexDifference(c.wide, c.strip);
      cheeks = subtractAll(cheeks, [...walkable, ...poolPolys]);
      for (const o of corridors) if (o !== c) cheeks = subtractAll(cheeks, [o.wide]);
      for (const rise of c.def.rises ?? []) cheeks = splitAtRise(cheeks, rise.start, rise.end, 1);
      for (const p of cheeks) addLedge(p, p.map((q) => corridorHeightAt(d, c.def, q) + ledgeH), c.id, c.cluster, true);
    }
  }
  const floorBoxes = floors.map((f) => ({ box: bbox(f.poly), plane: fitPlane(f.poly, f.h) }));
  /** Height of the rendered floor piece at p (stair steps are stepped, not the linear ramp). */
  const renderedFloorAt = (p: Pt): number => {
    for (let i = 0; i < floors.length; i++) {
      const fb = floorBoxes[i]!;
      if (inBox(fb.box, p, 0.001) && pointInConvex(p, floors[i]!.poly, 1e-6)) return planeAt(fb.plane, p.x, p.y);
    }
    return floorAt(p);
  };
  const ledgeTopAt = (p: Pt): number | null => {
    for (const l of ledges) if (inBox(l.box, p, 0.001) && pointInConvex(p, l.poly, 1e-6)) return planeAt(l.plane, p.x, p.y);
    return null;
  };
  const surfaceAt = (p: Pt): { kind: 'floor' | 'ledge' | 'pool' | 'void'; h: number } => {
    if (inWalkable(p)) return { kind: 'floor', h: renderedFloorAt(p) };
    for (const pool of pools) if (pointInConvex(p, pool.poly)) return { kind: 'pool', h: pool.waterY };
    const l = ledgeTopAt(p);
    if (l !== null) return { kind: 'ledge', h: l };
    return { kind: 'void', h: A.baseY };
  };
  const topNear = (p: Pt): number => {
    const l = ledgeTopAt(p);
    if (l !== null) return l;
    return floorAt(p) + ledgeH;
  };

  // ---- walls ------------------------------------------------------------------------
  const walls: WallPiece[] = [];
  const posts: Post[] = [];
  const layout = computeWallLayout(regions.map((r) => ({ points: r.poly })), 0.5, 0.3);
  const regionAt = (p: Pt, except: number): boolean => regions.some((r, i) => i !== except && pointInPoly(p, r.poly));
  for (const run of layout.walls) {
    const reg = regions[run.polyIndex]!;
    const len = Math.hypot(run.b.x - run.a.x, run.b.y - run.a.y);
    if (len < 0.2) continue;
    const n = Math.max(1, Math.round(len / 3));
    const dx = (run.b.x - run.a.x) / len;
    const dy = (run.b.y - run.a.y) / len;
    for (let k = 0; k < n; k++) {
      const a = { x: run.a.x + dx * ((k * len) / n), y: run.a.y + dy * ((k * len) / n) };
      const b = { x: run.a.x + dx * (((k + 1) * len) / n), y: run.a.y + dy * (((k + 1) * len) / n) };
      const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
      // Thinner where another region is close behind (two rooms' walls share a narrow gap).
      let thick = A.wallThickness;
      for (let t = 0.1; t <= A.wallThickness; t += 0.1) {
        if (regionAt({ x: mid.x + run.normal.x * t, y: mid.y + run.normal.y * t }, run.polyIndex)) {
          thick = Math.max(0.25, t - 0.05);
          break;
        }
      }
      const inner = { x: mid.x - run.normal.x * 0.3, y: mid.y - run.normal.y * 0.3 };
      const foot = Math.max(topNear(inner), topNear({ x: a.x - run.normal.x * 0.3, y: a.y - run.normal.y * 0.3 }), topNear({ x: b.x - run.normal.x * 0.3, y: b.y - run.normal.y * 0.3 }));
      const facing = facingOf(run.normal);
      const corridor = reg.kind === 'corridor';
      // Enclosed: full-height walls all round (the camera cutaway cuts the near ones down).
      const H = E ? (corridor ? corridorWallH(reg.owner) : roomWallH(reg.owner)) : A.wallHeights[facing] * (corridor ? 0.85 : 1);
      walls.push({ a, b, n: run.normal, thick, base: A.baseY, top: foot + H, foot, facing, owner: reg.owner, cluster: reg.cluster, corridor, cutY: E ? foot + E.cutHeight : foot + H });
    }
  }
  // Corner pillars (not inside another region) and opening posts on room outlines.
  if (ledgeH > 0) {
    for (const c of layout.corners) {
      const reg = regions[c.polyIndex]!;
      if (regionAt(c.pos, c.polyIndex) || inWalkable(c.pos)) continue;
      const nearWall = walls.filter((w) => Math.min(Math.hypot(w.a.x - c.pos.x, w.a.y - c.pos.y), Math.hypot(w.b.x - c.pos.x, w.b.y - c.pos.y)) < 0.6);
      const top = Math.max(...nearWall.map((w) => w.top), topNear(c.pos) + 1) + 0.45;
      posts.push({ pos: c.pos, size: 1.5, base: A.baseY, top, owner: reg.owner, cluster: reg.cluster, kind: 'corner', cutY: postCut(nearWall, c.pos), n: avgNormal(nearWall) });
    }
    for (const o of layout.openings) {
      const reg = regions[o.polyIndex]!;
      if (reg.kind !== 'room') continue;
      for (const p of [o.a, o.b]) {
        // Only where the opening meets a wall (not mid-opening splits).
        const touchesWall = walls.some((w) => w.owner === reg.owner && Math.min(Math.hypot(w.a.x - p.x, w.a.y - p.y), Math.hypot(w.b.x - p.x, w.b.y - p.y)) < 0.6);
        if (!touchesWall) continue;
        const q = { x: p.x + o.normal.x * 0.6, y: p.y + o.normal.y * 0.6 };
        if (inWalkable(q)) continue;
        const near = walls.filter((w) => Math.min(Math.hypot(w.a.x - p.x, w.a.y - p.y), Math.hypot(w.b.x - p.x, w.b.y - p.y)) < 1);
        const top = Math.max(...near.map((w) => w.top), topNear(p) + 2) + 0.7;
        posts.push({ pos: q, size: 1.7, base: A.baseY, top, owner: reg.owner, cluster: reg.cluster, kind: 'opening', cutY: postCut(near, p), n: avgNormal(near) });
      }
    }
  }

  // ---- enclosed interiors: lintels, ceilings, beams, colonnade, arcade roofs ---------------
  const enc = enclosedParts();

  return {
    rooms,
    corridors,
    floors,
    ledges,
    walls,
    posts,
    enclosed: !!E,
    ...enc,
    pools,
    regions,
    walkable,
    hints,
    clusterOf,
    clusterCount,
    baseY: A.baseY,
    ledgeHeight: ledgeH,
    ledgeTopAt,
    floorAt,
    renderedFloorAt,
    surfaceAt,
    inWalkable,
    inObstacle: (p: Pt): boolean => (d.obstacles ?? []).some((o) => pointInPoly(p, o.points)),
    distToWalkable,
  };

  /** Enclosed interiors: lintels over corridor mouths, ceilings, timber frames, colonnade, arcade. */
  function enclosedParts(): { lintels: Lintel[]; ceilings: Ceiling[]; beams: Beam[]; roofs: Roof[] } {
    const lintels: Lintel[] = [];
    const ceilings: Ceiling[] = [];
    const beams: Beam[] = [];
    const roofs: Roof[] = [];
    if (!E) return { lintels, ceilings, beams, roofs };
    const roomTop = (r: RoomArch): number => r.ledgeTop + roomWallH(r.id);
    const vaultOf = (c: CorridorArch): { kind: CeilingKind; H: number; span: number } => ({
      kind: corridorStyleById.get(c.id)?.ceiling ?? 'vault',
      H: corridorWallH(c.id),
      span: c.axis.width + 2 * cm,
    });
    const corridorRise = (kind: CeilingKind, span: number): number => Math.min(rise(kind, span), 3);
    const rise = (kind: CeilingKind, span: number): number => (kind === 'beams' || kind === 'none' ? 0 : span * E.vaultRise);

    // Timber frames (mine): a cross beam every `every` yd on two posts standing against the walls.
    const frames = (a: Pt, b: Pt, span: number, every: number, floor: (p: Pt) => number, top: (p: Pt) => number, owner: string, cluster: number): void => {
      const len = Math.hypot(b.x - a.x, b.y - a.y);
      if (len < 1) return;
      const dir = { x: (b.x - a.x) / len, y: (b.y - a.y) / len };
      const nrm = { x: -dir.y, y: dir.x };
      const n = Math.max(1, Math.floor(len / every));
      for (let k = 0; k < n; k++) {
        const t = ((k + 0.5) * len) / n;
        const c = { x: a.x + dir.x * t, y: a.y + dir.y * t };
        const y = top(c) - 0.65;
        const h = 0.6;
        const p0 = { x: c.x - nrm.x * (span / 2), y: c.y - nrm.y * (span / 2) };
        const p1 = { x: c.x + nrm.x * (span / 2), y: c.y + nrm.y * (span / 2) };
        beams.push({ a: p0, b: p1, y, h, w: 0.6, wood: true, owner, cluster });
        for (const [p, s] of [
          [p0, -1],
          [p1, 1],
        ] as const) {
          const q = { x: p.x - nrm.x * s * 0.45, y: p.y - nrm.y * s * 0.45 };
          if (inWalkable(q) || distToWalkable(q) < 0.35) continue;
          const f = floor(q);
          posts.push({ pos: q, size: 0.6, base: f - 0.2, top: y + h, owner, cluster, kind: 'timber', cutY: f + ledgeH + E.cutHeight, n: { x: nrm.x * s, y: nrm.y * s } });
        }
      }
    };

    // ---- lintels over corridor mouths (the room wall continues over the opening) ----------
    for (const o of layout.openings) {
      const reg = regions[o.polyIndex]!;
      if (reg.kind !== 'room') continue;
      const room = rooms.find((r) => r.id === reg.owner);
      if (!room) continue;
      const len = Math.hypot(o.b.x - o.a.x, o.b.y - o.a.y);
      if (len < 0.4) continue;
      const out = { x: (o.a.x + o.b.x) / 2 + o.normal.x * 1.2, y: (o.a.y + o.b.y) / 2 + o.normal.y * 1.2 };
      const c = corridors.find((x) => pointInPoly(out, x.wide));
      if (!c) continue;
      const v = vaultOf(c);
      const spring = corridorHeightAt(d, c.def, out) + ledgeH + v.H;
      const apex = spring + Math.min(corridorRise(v.kind, v.span), len / 2);
      // Where the corridor vault rises above the room's wall top (the low cloister walls), the
      // lintel becomes a gable over the doorway.
      const top = Math.max(roomTop(room), apex + 0.8);
      lintels.push({ a: o.a, b: o.b, n: o.normal, thick: A.wallThickness, spring, apex, top, foot: room.ledgeTop, owner: room.id, cluster: room.cluster });
    }

    // ---- room ceilings (vault along the long axis) and the cloister arcade -----------------
    for (const r of rooms) {
      const O = bbox(r.outer);
      const spring = roomTop(r);
      const arc = r.style.arcade;
      if (arc && r.rect) {
        const R = r.rect;
        const G = { x0: R.x0 + arc.inset, y0: R.y0 + arc.inset, x1: R.x1 - arc.inset, y1: R.y1 - arc.inset };
        const low = r.elevation + arc.low;
        const hi = spring - 0.15;
        roofs.push({ poly: rectPoly(O.x0, G.y1, O.x1, O.y1), h: [low, low, hi, hi], owner: r.id, cluster: r.cluster });
        roofs.push({ poly: rectPoly(O.x0, O.y0, O.x1, G.y0), h: [hi, hi, low, low], owner: r.id, cluster: r.cluster });
        roofs.push({ poly: rectPoly(O.x0, G.y0, G.x0, G.y1), h: [hi, low, low, hi], owner: r.id, cluster: r.cluster });
        roofs.push({ poly: rectPoly(G.x1, G.y0, O.x1, G.y1), h: [low, hi, hi, low], owner: r.id, cluster: r.cluster });
        // Architraves along the colonnade (stone beams the roof rests on).
        const corners = [
          { x: G.x0, y: G.y0 },
          { x: G.x1, y: G.y0 },
          { x: G.x1, y: G.y1 },
          { x: G.x0, y: G.y1 },
        ];
        for (let i = 0; i < 4; i++) beams.push({ a: corners[i]!, b: corners[(i + 1) % 4]!, y: low - 0.9, h: 0.9, w: 1.1, wood: false, owner: r.id, cluster: r.cluster });
        continue;
      }
      const kind = r.style.ceiling ?? 'vault';
      if (kind === 'none') continue;
      const alongX = O.x1 - O.x0 >= O.y1 - O.y0;
      const span = alongX ? O.y1 - O.y0 : O.x1 - O.x0;
      const a = alongX ? { x: O.x0, y: (O.y0 + O.y1) / 2 } : { x: (O.x0 + O.x1) / 2, y: O.y0 };
      const b = alongX ? { x: O.x1, y: a.y } : { x: a.x, y: O.y1 };
      ceilings.push({ kind, a, b, span, springA: spring, springB: spring, rise: Math.min(rise(kind, span), 10), ribEvery: kind === 'beams' ? 5 : 8, owner: r.id, cluster: r.cluster });
      if (kind === 'beams') frames(a, b, span, 5.5, () => r.elevation, () => spring, r.id, r.cluster);
    }

    // ---- corridor ceilings (outside both rooms; pieces follow the stairs) ------------------
    for (const c of corridors) {
      const v = vaultOf(c);
      if (v.kind === 'none') continue;
      const { a, dir, len } = c.axis;
      const at = (t: number): Pt => ({ x: a.x + dir.x * t, y: a.y + dir.y * t });
      const inOuter = (p: Pt): boolean => rooms.some((r) => pointInPoly(p, r.outer));
      let t0 = 0;
      let t1 = len;
      for (let t = 0; t <= len; t += 0.25)
        if (!inOuter(at(t))) {
          t0 = t;
          break;
        }
      for (let t = len; t >= 0; t -= 0.25)
        if (!inOuter(at(t))) {
          t1 = t;
          break;
        }
      if (t1 - t0 < 0.5) continue;
      const spring = (t: number): number => corridorHeightAt(d, c.def, at(t)) + ledgeH + v.H;
      const n = Math.max(1, Math.ceil((t1 - t0) / 2));
      for (let k = 0; k < n; k++) {
        const ta = t0 + ((t1 - t0) * k) / n;
        const tb = t0 + ((t1 - t0) * (k + 1)) / n;
        ceilings.push({ kind: v.kind, a: at(ta), b: at(tb), span: v.span, springA: spring(ta), springB: spring(tb), rise: corridorRise(v.kind, v.span), ribEvery: 0, owner: c.id, cluster: c.cluster });
      }
      // Ribs (stone vaults: a transverse arch band every ~6 yd) / timber frames (mine).
      if (v.kind === 'beams') frames(at(t0), at(t1), v.span, 4.5, (p) => corridorHeightAt(d, c.def, p), (p) => corridorHeightAt(d, c.def, p) + ledgeH + v.H, c.id, c.cluster);
      else {
        const ribs = Math.max(1, Math.floor((t1 - t0) / 6));
        for (let k = 0; k < ribs; k++) {
          const t = t0 + ((k + 0.5) * (t1 - t0)) / ribs;
          ceilings.push({ kind: v.kind, a: at(t - 0.35), b: at(t + 0.35), span: v.span - 0.1, springA: spring(t) - 0.3, springB: spring(t) - 0.3, rise: corridorRise(v.kind, v.span) - 0.05, ribEvery: -1, owner: c.id, cluster: c.cluster });
        }
      }
    }

    // ---- colonnade pillars (DungeonDef.obstacles without a hero prop inside) ---------------
    for (const ob of d.obstacles ?? []) {
      if (allHints.some((h) => pointInPoly(h.pos, ob.points))) continue;
      const bb = bbox(ob.points);
      const pos = { x: (bb.x0 + bb.x1) / 2, y: (bb.y0 + bb.y1) / 2 };
      const r = rooms.find((x) => pointInPoly(pos, x.outer));
      const f = floorAt(pos);
      const top = r?.style.arcade ? r.elevation + r.style.arcade.low - 0.9 : r ? roomTop(r) : f + 4;
      posts.push({ pos, size: Math.max(bb.x1 - bb.x0, bb.y1 - bb.y0), base: f - 0.1, top, owner: r?.id ?? '', cluster: r?.cluster ?? 0, kind: 'pillar', cutY: f + ledgeH + E.cutHeight });
    }
    return { lintels, ceilings, beams, roofs };
  }
}

const inBox = (b: Rect, p: Pt, pad: number): boolean => p.x >= b.x0 - pad && p.x <= b.x1 + pad && p.y >= b.y0 - pad && p.y <= b.y1 + pad;
const polyArea = (p: Poly): number => {
  let s = 0;
  for (let i = 0, j = p.length - 1; i < p.length; j = i++) s += p[j]!.x * p[i]!.y - p[i]!.x * p[j]!.y;
  return Math.abs(s / 2);
};

/** Parameter of p's projection on a→b (0 at a, 1 at b). */
export function tAlong(p: Pt, a: Pt, b: Pt): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const l2 = dx * dx + dy * dy;
  return l2 === 0 ? 0 : ((p.x - a.x) * dx + (p.y - a.y) * dy) / l2;
}

/** Split convex pieces with lines perpendicular to a→b at t = 0, 1/n, …, 1. */
function splitAtRise(pieces: Poly[], a: Pt, b: Pt, n: number): Poly[] {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  // Perpendicular cut line through point q: direction (-dy, dx).
  let cur = pieces;
  for (let k = 0; k <= n; k++) {
    const q = { x: a.x + (dx * k) / n, y: a.y + (dy * k) / n };
    const q2 = { x: q.x - dy, y: q.y + dx };
    const next: Poly[] = [];
    for (const p of cur) {
      const l = clipHalfPlane(p, q, q2, true);
      const r = clipHalfPlane(p, q, q2, false);
      if (l.length >= 3 && polyArea(l) > 0.005) next.push(l);
      if (r.length >= 3 && polyArea(r) > 0.005) next.push(r);
    }
    cur = next;
  }
  return cur;
}

/** Long axis of a corridor strip (4-point rectangle). */
function corridorAxis(strip: Poly): CorridorArch['axis'] {
  const e = (i: number): number => Math.hypot(strip[(i + 1) % 4]!.x - strip[i]!.x, strip[(i + 1) % 4]!.y - strip[i]!.y);
  const long = e(0) >= e(1) ? 0 : 1;
  const p0 = strip[long]!;
  const p1 = strip[(long + 1) % 4]!;
  const p3 = strip[(long + 3) % 4]!;
  const width = Math.hypot(p3.x - p0.x, p3.y - p0.y);
  const nx = (p3.x - p0.x) / (width || 1);
  const ny = (p3.y - p0.y) / (width || 1);
  const a = { x: p0.x + (nx * width) / 2, y: p0.y + (ny * width) / 2 };
  const b = { x: p1.x + (nx * width) / 2, y: p1.y + (ny * width) / 2 };
  const len = Math.hypot(b.x - a.x, b.y - a.y);
  return { a, b, dir: { x: (b.x - a.x) / (len || 1), y: (b.y - a.y) / (len || 1) }, len, width };
}
